import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { GenericErrorStateMatcher } from 'src/app/shared/attribute-state-generators/generic-error.state-matcher';
import { MailingAddress } from 'src/app/shared/mailing-address-autocomplete/mailing-address-autocomplete.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BuildingHutsIdResponseModel } from './models/buildingHutsId.response.model';
import { BuildingSearchService } from './building-search.service';
import { BuildingAddressModel } from './models/building-address.model';

@Component({
  selector: 'app-building-search',
  styleUrls: ['./building-search.component.scss'],
  templateUrl: './building-search.component.html'
})
export class BuildingSearchComponent implements OnInit {
  @Input() isDisabled = false;

  @Input() searchType: string;
  @Input() building: BuildingHutsIdResponseModel;
  transactionId: string;
  form: FormGroup = BuildingAddressModel.asForm(this.fb);
  mailingAddress: MailingAddress;
  buildingSearchForm: FormGroup;

  @Output() selectedBuilding: EventEmitter<{ value: BuildingHutsIdResponseModel, type: string }> =
    new EventEmitter<{ value: BuildingHutsIdResponseModel, type: string }>(null);
  buildingHutsId: number;
  buildingHutsIdResultsSubject: BehaviorSubject<BuildingHutsIdResponseModel[]> = new BehaviorSubject<BuildingHutsIdResponseModel[]>(null);
  buildingHutsIdResults$: Observable<BuildingHutsIdResponseModel[]> = this.buildingHutsIdResultsSubject.asObservable();
  selectedBuildingAddress: BuildingHutsIdResponseModel;
  selectedAddressFormSubject: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
  selectedAddressForm$: Observable<FormGroup> = this.selectedAddressFormSubject.asObservable();
  addressSearchBuildingDetailsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  addressSearchBuildingDetails$: Observable<boolean> = this.addressSearchBuildingDetailsSubject.asObservable();
  buildingSearchFormSubject: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
  buildingSearchForm$: Observable<FormGroup> = this.buildingSearchFormSubject.asObservable();
  isAdvancedSearch = false;
  buildingSearchType: string;
  isBuildingNotSelected$ = this.buildingSearchService.isBuildingNotSelected$;
  constructor(
    private fb: FormBuilder,
    private buildingSearchService: BuildingSearchService,
    private toastrService: ToastrService,
    private ref: ChangeDetectorRef
  ) {

  }

  ngOnInit() {
    if (this.isDisabled) {
      this.isAdvancedSearch = false;
    }

    this.buildingSearchType = this.searchType ? this.searchType : 'address';
    if (this.building) {
      this.selectedAddressFormSubject.next(BuildingHutsIdResponseModel.generateForm(this.fb, this.building, this.isDisabled));
    }
    this.ref.detectChanges();


  }

  buildingSearchById() {
    this.selectedAddressFormSubject.next(null);
    if (this.buildingHutsId) {
      this.buildingSearchService.getBuildingDetailsById(this.buildingHutsId).subscribe(response => {
        this.buildingHutsIdResultsSubject.next(response);
      }, errors => {
        this.toastrService.error('Unable to retrieve the Building Details.');
      });
    }
  }

  addressSelected() {
    this.selectedAddressFormSubject.next(BuildingHutsIdResponseModel.generateForm(this.fb, this.selectedBuildingAddress, this.isDisabled));
    const response = {
      value: this.selectedBuildingAddress,
      type: 'buildingId'
    };
    this.selectedBuilding.emit(response);
  }

  isMailingAddressVerified() {
    // For our purposes we will assume the presence of core mailing address fields
    // indicates the address was verified. We are not setting these fields until
    // Smarty Streets address verification occurs...
    return this.form.controls.businessAddress.get('address1').value != null &&
      this.form.controls.businessAddress.get('city').value != null &&
      this.form.controls.businessAddress.get('state').value != null &&
      this.form.controls.businessAddress.get('zip').value != null;
  }

  onMailingAddressValidated(value) {
    // Hold onto address info, may need it for error messages...
    this.mailingAddress = value ? value.mailingAddress : '';
    // Check if address was valid...
    if (value && value.mailingAddress && value.mailingAddress.isValid) {
      // Set building address form control values using SmartyStreets autocomplete results...
      this.buildingSearchService.getBuildingId(value.address).subscribe(response => {
        if (response.length > 0) {
          this.addressSearchBuildingDetailsSubject.next(false);
          const form = BuildingHutsIdResponseModel.generateForm(this.fb, response[0], this.isDisabled);
          this.selectedAddressFormSubject.next(form);
          this.selectedBuilding.emit({ value: response[0], type: 'address' });
        } else {
          this.addressSearchBuildingDetailsSubject.next(true);
          this.selectedAddressFormSubject.next(null);
          const buildingResponse = {
            value: null,
            type: 'buildingId'
          };
          this.selectedBuilding.emit(buildingResponse);
        }
      });
    } else {
      // Indicate address was not verified...
      this.selectedAddressFormSubject.next(null);
      this.form.controls.isBussinessAddressVerified.setValue(false);
    }
  }

  onMailingAddressChanged() {
    this.selectedBuilding.emit(null);
    // Anytime the mailing address is changed (keypress detected) clear existing form control
    // values (they will be set again once a mailing address is selected...
    this.form.controls.isBussinessAddressVerified.setValue(null);
    this.form.controls.businessAddress.get('address1').setValue(null);
    this.form.controls.businessAddress.get('address2').setValue(null);
    this.form.controls.businessAddress.get('city').setValue(null);
    this.form.controls.businessAddress.get('state').setValue(null);
    this.form.controls.businessAddress.get('zip').setValue(null);
  }

  public generateErrorStateMatcher(targetParentFormErrors?: string[]): GenericErrorStateMatcher {
    return new GenericErrorStateMatcher(targetParentFormErrors);
  }

  tryAdvancedSearch() {
    this.isAdvancedSearch = true;
  }

  cancelAdvancedSearch(value) {
    this.selectedAddressFormSubject.next(null);
    this.form = BuildingAddressModel.asForm(this.fb);
    this.addressSearchBuildingDetailsSubject.next(false);
    this.isAdvancedSearch = value;
  }

  typeOfSearch(value) {
    this.buildingSearchType = value;
    /* this.buildingSearchForm = this.fb.group({
      button: [{value}]
    });
    this.buildingSearchFormSubject.next(this.buildingSearchForm); */
    this.ref.detectChanges();
    this.selectedAddressFormSubject.next(null);
    this.form = BuildingAddressModel.asForm(this.fb);
  }
  buildingSelected(value) {
    const response = {
      value,
      type: 'buildingId'
    };
    this.selectedBuilding.emit(response);
  }
}
